import { useQuery } from "@connectrpc/connect-query";
import { skipToken } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";

import { formatNepalTime, nepalToday } from "@yatayat/core/date";
import { formatNumber, formatPaisa } from "@yatayat/core/money";
import { BookingService, type TripOffer } from "@yatayat/gen/yatayat/booking/v1/booking_pb";

import { BsDateHint } from "~/components/BsDateHint";
import { PlaceSelect } from "~/components/PlaceSelect";
import { Empty, Loading, QueryError } from "~/components/QueryState";
import { OfficialFareBadge, PermitVerifiedBadge } from "~/components/TripBadges";
import { useI18n } from "~/lib/i18n";
import { vehicleClassLabels } from "~/lib/labels";
import { useSortedPlaces } from "~/lib/places";
import { retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { ui } from "~/lib/ui";

type BookSearch = { from?: string; to?: string; date?: string };

// The search lives in the URL so going back from a seat map restores the results.
export const Route = createFileRoute("/book/")({
  validateSearch: (search: Record<string, unknown>): BookSearch => ({
    from: searchString(search.from),
    to: searchString(search.to),
    date: searchString(search.date),
  }),
  component: BookSearchPage,
});

const isoDate = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;

function BookSearchPage() {
  const { t } = useI18n();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const placeList = useSortedPlaces();
  const places = placeList.places;

  const today = nepalToday();
  const from = search.from ?? "";
  const to = search.to ?? "";
  const date = search.date ?? today;

  const update = (patch: BookSearch) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false });

  const ready = from !== "" && to !== "" && from !== to && isoDate.test(date);
  const trips = useQuery(
    BookingService.method.searchTrips,
    ready ? { fromCode: from, toCode: to, date } : skipToken,
    { retry: retryTransient },
  );

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className={ui.h1}>{t("bookSeat")}</h1>
        <p className={ui.lead}>{t("bookIntro")}</p>
      </div>

      <form className="grid gap-4 sm:grid-cols-3" onSubmit={(e) => e.preventDefault()}>
        <PlaceSelect
          label={t("from")}
          value={from}
          onChange={(code) => update({ from: code || undefined })}
          places={places}
          placeholder={placeList.isPending ? t("loading") : t("choosePlace")}
        />
        <PlaceSelect
          label={t("to")}
          value={to}
          onChange={(code) => update({ to: code || undefined })}
          places={places}
          placeholder={placeList.isPending ? t("loading") : t("choosePlace")}
        />
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t("travelDate")}</span>
          <input
            type="date"
            value={date}
            min={today}
            onChange={(e) => update({ date: e.target.value || undefined })}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
          />
          <BsDateHint date={date} />
        </label>
      </form>

      {placeList.isError && <QueryError onRetry={() => void placeList.refetch()} busy={placeList.isFetching} />}

      {from !== "" && from === to && <p className={ui.error}>{t("samePlace")}</p>}

      <section aria-live="polite" aria-busy={trips.isFetching}>
        {trips.isLoading && <Loading />}
        {trips.isError && <QueryError onRetry={() => void trips.refetch()} busy={trips.isFetching} />}
        {ready && trips.data?.trips.length === 0 && <Empty>{t("noTrips")}</Empty>}
        {ready && trips.data && trips.data.trips.length > 0 && (
          <ul className="space-y-3">
            {trips.data.trips.map((trip) => (
              <TripCard key={trip.id} trip={trip} from={from} to={to} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function TripCard({ trip, from, to }: { trip: TripOffer; from: string; to: string }) {
  const { t, lang } = useI18n();
  const soldOut = trip.seatsLeft <= 0;

  return (
    <li className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className={ui.h1}>
            <time dateTime={new Date(Number(trip.departsAtMs)).toISOString()}>
              {formatNepalTime(Number(trip.departsAtMs), lang)}
            </time>
          </p>
          <p className="font-medium">{trip.operatorName?.[lang]}</p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {trip.routeName?.[lang] && `${trip.routeName[lang]} · `}
            {t(vehicleClassLabels[trip.vehicleClass])}
            {trip.electric && ` · ${t("electric")}`}
          </p>
          <p className="mt-1 text-sm">
            {soldOut
              ? t("soldOut")
              : t("seatsLeft", { left: formatNumber(trip.seatsLeft, lang), total: formatNumber(trip.seatCount, lang) })}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xl font-semibold">{formatPaisa(trip.officialFare?.paisa ?? 0n, lang)}</p>
          <div className="mt-1">
            <OfficialFareBadge />
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {trip.permitVerified && <PermitVerifiedBadge />}
        {!soldOut && (
          <Link
            to="/book/$tripId"
            params={{ tripId: trip.id }}
            search={{ from, to }}
            className="ml-auto rounded-md bg-ink px-4 py-2 text-sm font-medium text-white dark:bg-ink-dark dark:text-slate-950"
          >
            {t("chooseSeat")}
          </Link>
        )}
      </div>
    </li>
  );
}
